import { useState } from "react";
import { useDocumentFile, useRequestApproval, useRequestReject } from "./queries";
import { downloadFileFromUrl } from "../utils/downloadFileFromUrl";

const CLOSED_NOTIFICATION = { open: false, error: false, title: "", message: "" };

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

export function useLibraryRequestActions({ requestId, documentId, onChanged }) {
  const { mutate: approve, isPending: isAccepting } = useRequestApproval();
  const { mutate: reject, isPending: isRejecting } = useRequestReject();
  const { mutate: getFile, isPending: isFetchingFile } = useDocumentFile();
  const [notification, setNotification] = useState(CLOSED_NOTIFICATION);

  const notifySuccess = (message) =>
    setNotification({ open: true, error: false, title: "Success", message });
  const notifyError = (message) =>
    setNotification({ open: true, error: true, title: "Error", message });

  const accept = () =>
    approve(requestId, {
      onSuccess: () => {
        notifySuccess("The library request has been accepted.");
        onChanged?.();
      },
      onError: (error) =>
        notifyError(errorMessage(error, "The library request could not be accepted.")),
    });

  const submitReject = ({ reason }) =>
    reject(
      { id: requestId, reason },
      {
        onSuccess: () => {
          notifySuccess("The library request has been rejected.");
          onChanged?.();
        },
        onError: (error) =>
          notifyError(errorMessage(error, "The library request could not be rejected.")),
      }
    );

  const download = () =>
    getFile(documentId, {
      onSuccess: (file) =>
        downloadFileFromUrl(file?.download_link).catch(() =>
          notifyError("The report could not be downloaded.")
        ),
      onError: (error) => notifyError(errorMessage(error, "The report could not be downloaded.")),
    });

  const setNotificationOpen = (open) => setNotification((current) => ({ ...current, open }));

  return {
    accept,
    submitReject,
    download,
    notification,
    setNotificationOpen,
    isResponding: isAccepting || isRejecting,
    isDownloading: isFetchingFile,
  };
}
